import React, { useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import PartnerEngagements from '../components/Succeedo/PartnerWorkspace/PartnerEngagements';

const ENGAGEMENTS_QUERY = gql`
  query MyEngagements {
    currentUser {
      id
      name
      succeedo_persona
      action_plan {
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    my_engagements
  }
`;

const ACCEPT_INVITE = gql`
  mutation AcceptDealTeamInvite($id: ID!, $token: String!) {
    accept_deal_team_invite(deal_team_member_id: $id, token: $token) {
      deal_team_member { id status }
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function PartnerEngagementsLoader() {
  const { data, loading, refetch } = useQuery(ENGAGEMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [acceptInvite, { loading: accepting }] = useMutation(ACCEPT_INVITE);
  const [acceptError, setAcceptError] = useState(null);

  const handleAcceptInvite = async ({ memberId, token }) => {
    setAcceptError(null);
    try {
      const { data: result } = await acceptInvite({
        variables: { id: memberId, token },
      });
      const errors = result?.accept_deal_team_invite?.errors || [];
      if (errors.length) {
        setAcceptError(errors.join(', '));
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_invite_accepted', { deal_team_member_id: memberId });
      }
      refetch();
    } catch (e) {
      setAcceptError(e?.message || 'Could not accept invitation. Please try again.');
    }
  };

  const user = data?.currentUser;
  const userName = user?.name || '';
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;
  const total = Math.max(openCount + completedCount, 5);
  const planProgress = {
    completed: completedCount,
    total,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
  };

  return (
    <PartnerEngagements
      engagements={data?.my_engagements || []}
      loading={loading}
      persona="PARTNER"
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={openCount}
      onAcceptInvite={handleAcceptInvite}
      accepting={accepting}
      acceptError={acceptError}
    />
  );
}

const SucceedoPartnerEngagementsContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <PartnerEngagementsLoader />
  </ApolloProvider>
);

export default SucceedoPartnerEngagementsContainer;
